import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, RefreshCw, BookOpen, CheckCircle, XCircle, Clock, TrendingUp, BarChart3, Upload } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ImportTradesModal } from "@/components/ImportTradesModal";
import { toast } from "sonner";
import { TradeLogTable, DEFAULT_GRID_PREFS } from "@/components/TradeLogTable";

const GRID_PREFS_KEY = "ml-tradelog-grid-v1";

function ManageImportsDialog({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [batches, setBatches] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const loadBatches = () => api.get("/trade-log/import/batches")
    .then(({ data }) => setBatches(data.batches || [])).catch(() => setBatches([]));
  const openDialog = (v) => { setOpen(v); setConfirmId(null); if (v) { setBatches(null); loadBatches(); } };
  const del = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.delete(`/trade-log/import/batch/${id}`);
      toast.success(`Removed ${data.deleted} trade${data.deleted === 1 ? "" : "s"}${data.positions_deleted ? ` + ${data.positions_deleted} synced position${data.positions_deleted === 1 ? "" : "s"}` : ""}`);
      try { sessionStorage.removeItem("ml-last-import"); } catch { /* ignore */ }
      await loadBatches();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); setConfirmId(null); }
  };
  return (
    <Dialog open={open} onOpenChange={openDialog}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-8 rounded-sm text-xs text-[#A1A1AA] hover:bg-[#1A1A1A]" data-testid="manage-imports-btn">
          <Trash2 className="w-3 h-3 mr-1" /> Manage imports
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111111] border-[#27272A] text-[#F4F4F5] max-w-lg" aria-describedby="manage-imports-desc">
        <DialogHeader><DialogTitle className="text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>Manage imported trade logs</DialogTitle></DialogHeader>
        <p id="manage-imports-desc" className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          Deleting an import removes its trades and any portfolio positions synced from them. Manual entries are never touched.
        </p>
        {batches === null ? (
          <div className="text-xs py-4 text-center" style={{ color: "var(--hoip-text-tertiary)" }}>Loading…</div>
        ) : batches.length === 0 ? (
          <div className="text-xs py-4 text-center" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="no-imports-msg">No imported batches.</div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="import-batches-list">
            {batches.map(b => (
              <div key={b.batch_id} className="flex items-center justify-between gap-2 border border-[#27272A] rounded-sm px-3 py-2" data-testid={`import-batch-row-${b.batch_id}`}>
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">Import {b.imported_at?.slice(0, 10) || "?"} · {b.count} trades</div>
                  <div className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {b.first_date?.slice(0, 10) || "?"} → {b.last_date?.slice(0, 10) || "?"}
                  </div>
                </div>
                {confirmId === b.batch_id ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => del(b.batch_id)} disabled={busyId === b.batch_id}
                      className="h-7 rounded-sm text-xs text-red-400 hover:bg-red-900/20" data-testid={`confirm-delete-batch-${b.batch_id}`}>
                      {busyId === b.batch_id ? "Deleting…" : "Confirm delete"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)} disabled={busyId === b.batch_id}
                      className="h-7 rounded-sm text-xs" data-testid={`cancel-delete-batch-${b.batch_id}`}>Cancel</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(b.batch_id)}
                    className="h-7 rounded-sm text-xs text-red-400 hover:bg-red-900/20 shrink-0" data-testid={`delete-batch-btn-${b.batch_id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UndoImportButton({ onUndone }) {
  const [last, setLast] = useState(() => {
    try {
      const s = JSON.parse(sessionStorage.getItem("ml-last-import") || "null");
      return s && Date.now() - s.ts < 24 * 3600 * 1000 ? s : null;
    } catch { return null; }
  });
  const [busy, setBusy] = useState(false);
  if (!last) return null;
  const undo = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/trade-log/import/batch/${last.batch_id}`);
      toast.success(`Removed ${data.deleted} imported trade${data.deleted === 1 ? "" : "s"}`);
      try { sessionStorage.removeItem("ml-last-import"); } catch { /* ignore */ }
      setLast(null);
      onUndone && onUndone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="ghost" onClick={undo} disabled={busy}
      className="h-8 rounded-sm text-xs text-red-400 hover:bg-red-900/20" data-testid="undo-import-btn">
      {busy ? "Removing…" : `Undo last import (${last.count})`}
    </Button>
  );
}

const STRATEGIES = [
  "bull_put_spread", "bull_call_spread", "cash_secured_put",
  "covered_call", "long_stock", "long_call", "long_put",
  "short_put", "short_call", "iron_condor", "bear_put_spread",
  "bear_call_spread", "collar", "straddle", "strangle"
];

export default function TradeLogPage() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [search, setSearch] = useState("");
  const [gridPrefs, setGridPrefs] = useState(() => {
    try {
      const raw = sessionStorage.getItem(GRID_PREFS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        return { ...DEFAULT_GRID_PREFS, ...p, sort: { ...DEFAULT_GRID_PREFS.sort, ...(p.sort || {}) } };
      }
    } catch { /* ignore corrupt prefs */ }
    return DEFAULT_GRID_PREFS;
  });
  const updateGridPrefs = (next) => {
    setGridPrefs(next);
    try { sessionStorage.setItem(GRID_PREFS_KEY, JSON.stringify(next)); } catch { /* quota */ }
  };
  const [form, setForm] = useState({
    ticker: "", strategy: "bull_put_spread", entry_date: new Date().toISOString().split("T")[0],
    exit_date: "", entry_price: "", exit_price: "", pnl: "",
    assigned: false, outcome_notes: "", status: "open",
    short_strike: "", long_strike: "", expiry: "", qty: "1",
    exit_reason: "", premium_direction: "credit"
  });

  // ─── Iter170 sign-convention helpers (must match backend
  //     services/premium_math.py so UX + storage stay in lockstep) ───
  const CREDIT_STRATS = new Set([
    "bull_put_spread", "bear_call_spread", "iron_condor", "iron_butterfly",
    "short_put", "cash_secured_put", "short_call", "covered_call",
    "strangle", "straddle",
  ]);
  const DEBIT_STRATS = new Set([
    "long_put", "long_call", "bull_call_spread", "bear_put_spread",
    "protective_put", "married_put", "collar",
    "calendar", "calendar_spread", "diagonal", "diagonal_spread",
  ]);
  const inferDirection = (strategy) => {
    if (CREDIT_STRATS.has(strategy)) return "credit";
    if (DEBIT_STRATS.has(strategy)) return "debit";
    return "credit";
  };
  const isOptionStrategy = (strategy) =>
    strategy && strategy !== "long_stock" &&
    (CREDIT_STRATS.has(strategy) || DEBIT_STRATS.has(strategy));

  const load = async () => {
    try {
      const { data } = await api.get("/trade-log");
      setTrades(data.trades || []);
    } catch (err) { console.error("Trade log error:", err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Iter141: intercept ?action=close|roll deep-links from Wheel Strategies
  // LegRow (replacing the removed tastyworks broker links). Pre-fills the
  // dialog with the source leg's fields so the user reviews before writing
  // to trade_log — matches the "user retains final control" principle.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const action = searchParams.get("action");
    if (!action) return;
    const tk = (searchParams.get("ticker") || "").toUpperCase();
    const strat = searchParams.get("strategy") || "bull_put_spread";
    const sh = searchParams.get("short_strike") || "";
    const lg = searchParams.get("long_strike") || "";
    const exp = searchParams.get("expiry") || "";
    const qty = searchParams.get("qty") || "1";
    if (action === "close") {
      const entry = searchParams.get("entry_price") || "";
      const mid = searchParams.get("current_mid") || "";
      const tid = searchParams.get("trade_id") || "";
      setForm({
        ticker: tk, strategy: strat,
        // Iter141 nit-fix: don't default entry_date to today for close —
        // deep-link doesn't carry the real entry date and defaulting it
        // to today would make the trade log record today as both entry
        // and exit. Leave blank so user can fill from their record.
        entry_date: "",
        exit_date: new Date().toISOString().split("T")[0],
        entry_price: entry, exit_price: mid, pnl: "",
        assigned: false,
        outcome_notes: `Closed via Wheel Strategies advisor${tid ? ` (source trade ${tid.slice(0, 8)})` : ""} — exit_price is estimated market mid, adjust to actual fill`,
        status: "closed",
        short_strike: sh, long_strike: lg, expiry: exp, qty,
        exit_reason: "Closed Early",
        premium_direction: inferDirection(strat),
      });
      setEditingTrade(tid ? { id: tid, __from_url: true } : null);
      setShowDialog(true);
    } else if (action === "roll") {
      const parent = searchParams.get("parent_trade_id") || "";
      setForm({
        ticker: tk, strategy: strat,
        entry_date: new Date().toISOString().split("T")[0],
        exit_date: "", entry_price: "", exit_price: "", pnl: "",
        assigned: false,
        outcome_notes: `Rolled from source trade${parent ? ` ${parent.slice(0, 8)}` : ""} — new expiry & strikes to be entered`,
        status: "open",
        short_strike: sh, long_strike: lg, expiry: "", qty,
        exit_reason: "",
        premium_direction: inferDirection(strat),
      });
      setEditingTrade(null);
      setShowDialog(true);
    }
    // Clear params so re-opening the page doesn't re-trigger.
    setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const resetForm = () => setForm({
    ticker: "", strategy: "bull_put_spread", entry_date: new Date().toISOString().split("T")[0],
    exit_date: "", entry_price: "", exit_price: "", pnl: "",
    assigned: false, outcome_notes: "", status: "open",
    short_strike: "", long_strike: "", expiry: "", qty: "1",
    exit_reason: "", premium_direction: "credit"
  });

  const handleSubmit = async () => {
    try {
      const isOpt = isOptionStrategy(form.strategy);
      // Iter170 — always send positive magnitudes for options; direction
      // carried explicitly in premium_direction. For stock, no direction.
      const entryVal = form.entry_price === "" ? NaN : parseFloat(form.entry_price);
      const exitVal = form.exit_price === "" ? null : parseFloat(form.exit_price);
      const payload = {
        ticker: form.ticker.toUpperCase(), strategy: form.strategy, entry_date: form.entry_date,
        exit_date: form.exit_date || null,
        entry_price: isOpt && !Number.isNaN(entryVal) ? Math.abs(entryVal) : entryVal,
        exit_price: exitVal === null ? null : (isOpt ? Math.abs(exitVal) : exitVal),
        pnl: form.pnl ? parseFloat(form.pnl) : null, assigned: form.assigned,
        outcome_notes: form.outcome_notes, status: form.status,
        short_strike: form.short_strike ? parseFloat(form.short_strike) : null,
        long_strike: form.long_strike ? parseFloat(form.long_strike) : null,
        expiry: form.expiry || null,
        qty: form.qty ? parseInt(form.qty) : 1,
        exit_reason: form.exit_reason || null,
        premium_direction: isOpt ? form.premium_direction : null,
        margin_used: form.margin_used ? parseFloat(form.margin_used) : null,
      };
      if (editingTrade) { await api.put(`/trade-log/${editingTrade.id}`, payload); }
      else { await api.post("/trade-log", payload); }
      setShowDialog(false); setEditingTrade(null); resetForm(); load();
    } catch (err) { console.error("Trade log submit error:", err); }
  };

  const handleEdit = (trade) => {
    setEditingTrade(trade);
    setForm({
      ticker: trade.ticker || "", strategy: trade.strategy || "bull_put_spread",
      entry_date: trade.entry_date || "", exit_date: trade.exit_date || "",
      entry_price: trade.entry_price?.toString() || "", exit_price: trade.exit_price?.toString() || "",
      pnl: trade.pnl?.toString() || "", assigned: trade.assigned || false,
      outcome_notes: trade.outcome_notes || "", status: trade.status || "open",
      short_strike: trade.short_strike?.toString() || "", long_strike: trade.long_strike?.toString() || "",
      expiry: trade.expiry || "", qty: trade.qty?.toString() || "1",
      exit_reason: trade.exit_reason || "",
      margin_used: trade.margin_used?.toString() || "",
      // Iter170: use stored direction when available, else infer from strategy.
      premium_direction: trade.premium_direction || inferDirection(trade.strategy || ""),
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/trade-log/${id}`); load(); }
    catch (err) { console.error("Delete trade error:", err); }
  };

  const handleCloseTrade = async (trade) => {
    const pnl = prompt("Enter P&L for this trade ($):");
    if (pnl === null) return;
    try {
      await api.put(`/trade-log/${trade.id}`, { status: "closed", exit_date: new Date().toISOString().split("T")[0], pnl: parseFloat(pnl) || 0 });
      load();
    } catch (err) { console.error("Close trade error:", err); }
  };

  if (loading) return (<div className="flex items-center justify-center h-full p-8"><RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>);

  const openTrades = trades.filter(t => t.status === "open");
  const closedTrades = trades.filter(t => t.status === "closed");
  const totalPnl = closedTrades.reduce((s, t) => s + (t.pnl || 0), 0);
  const winCount = closedTrades.filter(t => (t.pnl || 0) > 0).length;
  const winRate = closedTrades.length >= 5 ? ((winCount / closedTrades.length) * 100).toFixed(1) : null;
  const winRateDisplay = winRate !== null ? `${winRate}%` : "—";
  const winRateLabel = closedTrades.length >= 5 ? winRateDisplay : "Insufficient sample";

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="trade-log-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-500" />
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Trade Log</h1>
        </div>
        <div className="flex items-center gap-2">
        <UndoImportButton onUndone={load} />
        <ManageImportsDialog onChanged={load} />
        <Button size="sm" variant="outline" onClick={() => setShowImport(true)}
          className="h-8 rounded-sm text-xs border-[#27272A] hover:bg-[#1A1A1A]" data-testid="import-ibkr-btn">
          <Upload className="w-3 h-3 mr-1" /> Import broker trade log
        </Button>
        <ImportTradesModal open={showImport} onOpenChange={setShowImport} onImported={load} />
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditingTrade(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button size="sm" className="h-8 bg-white text-black hover:bg-gray-200 rounded-sm text-xs" data-testid="add-trade-btn">
              <Plus className="w-3 h-3 mr-1" /> Log Trade
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111111] border-[#27272A] text-[#F4F4F5] max-w-md" aria-describedby="trade-dialog-desc">
            <DialogHeader><DialogTitle className="text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>{editingTrade ? "Edit Trade" : "Log New Trade"}</DialogTitle></DialogHeader>
            <p id="trade-dialog-desc" className="sr-only">Form to log a trade entry or exit</p>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Ticker</Label>
                  <Input data-testid="trade-ticker" value={form.ticker} onChange={(e) => setForm({...form, ticker: e.target.value})} placeholder="AAPL" className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Strategy</Label>
                  <select data-testid="trade-strategy" value={form.strategy}
                    onChange={(e) => {
                      // Iter170: switching strategy auto-updates the direction default
                      // so the user gets the correct sign for that leg type.
                      const s = e.target.value;
                      setForm({...form, strategy: s, premium_direction: inferDirection(s)});
                    }}
                    className="w-full h-8 px-2 text-xs font-mono-data rounded-sm border outline-none appearance-none cursor-pointer capitalize" style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
                    {STRATEGIES.map(s => (<option key={s} value={s} style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>{s.replace(/_/g, " ")}</option>))}
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Entry Date</Label><Input data-testid="trade-entry-date" type="date" value={form.entry_date} onChange={(e) => setForm({...form, entry_date: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Exit Date</Label><Input data-testid="trade-exit-date" type="date" value={form.exit_date} onChange={(e) => setForm({...form, exit_date: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
              </div>
              {/* Iter170 — direction toggle for options only. Positive
                  magnitude entered in the fields below; direction stored
                  explicitly and propagated to Existing Positions / margin
                  / cashflow math. See services/premium_math.py. */}
              {isOptionStrategy(form.strategy) && (
                <div data-testid="premium-direction-row" className="flex items-center gap-2">
                  <Label className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>
                    Premium Direction
                  </Label>
                  <div className="inline-flex border rounded-sm overflow-hidden"
                    style={{ borderColor: "var(--hoip-border)" }}>
                    {[
                      { v: "credit", label: "Credit (received)", cls: "text-emerald-400" },
                      { v: "debit", label: "Debit (paid)", cls: "text-red-400" },
                    ].map(opt => (
                      <button key={opt.v} type="button"
                        data-testid={`premium-direction-${opt.v}`}
                        onClick={() => setForm({...form, premium_direction: opt.v})}
                        className={`px-2.5 h-7 text-[10px] font-mono-data font-bold border-r last:border-r-0 ${
                          form.premium_direction === opt.v
                            ? `bg-[#1A1A1A] ${opt.cls}`
                            : "text-zinc-500 hover:text-zinc-300"
                        }`}
                        style={{ borderColor: "var(--hoip-border)" }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                    Enter positive per-share $ below. Auto-set from strategy — override if needed.
                  </span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>
                    Entry $ / share
                  </Label>
                  <Input data-testid="trade-entry-price" type="number" step="0.01" min="0"
                    value={form.entry_price}
                    onChange={(e) => setForm({...form, entry_price: e.target.value})}
                    className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" />
                  <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {isOptionStrategy(form.strategy)
                      ? "Per share, positive $. Sign comes from direction above."
                      : "Per share $ (stock)."}
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>
                    Exit $ / share
                  </Label>
                  <Input data-testid="trade-exit-price" type="number" step="0.01" min="0"
                    value={form.exit_price}
                    onChange={(e) => setForm({...form, exit_price: e.target.value})}
                    className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>P&amp;L $ (total)</Label>
                  <Input data-testid="trade-pnl" type="number" step="0.01" value={form.pnl}
                    onChange={(e) => setForm({...form, pnl: e.target.value})}
                    className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" />
                  <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
                    Total realized $ across all contracts.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Short Strike</Label><Input data-testid="trade-short-strike" type="number" step="0.5" value={form.short_strike} onChange={(e) => setForm({...form, short_strike: e.target.value})} placeholder="—" className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Long Strike</Label><Input data-testid="trade-long-strike" type="number" step="0.5" value={form.long_strike} onChange={(e) => setForm({...form, long_strike: e.target.value})} placeholder="—" className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Expiry</Label><Input data-testid="trade-expiry" type="date" value={form.expiry} onChange={(e) => setForm({...form, expiry: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Contracts</Label><Input data-testid="trade-qty" type="number" value={form.qty} onChange={(e) => setForm({...form, qty: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Margin Used $ (optional)</Label>
                  <Input data-testid="trade-margin-used" type="number" step="1" min="0" value={form.margin_used}
                    onChange={(e) => setForm({...form, margin_used: e.target.value})} placeholder="—"
                    className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" />
                  <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
                    Actual capital/margin deployed. Improves Return-on-Capital accuracy; blank = cash-secured estimate.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Status</Label>
                  <select data-testid="trade-status" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full h-8 px-2 text-xs font-mono-data rounded-sm border outline-none appearance-none cursor-pointer" style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
                    <option value="open" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>Open</option>
                    <option value="closed" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>Closed</option>
                    <option value="assigned" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>Assigned</option>
                  </select></div>
                <div className="flex items-center gap-2 pt-5"><Switch checked={form.assigned} onCheckedChange={(v) => setForm({...form, assigned: v})} data-testid="trade-assigned" /><Label className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>Assigned</Label></div>
              </div>
              {(form.status === "closed" || form.assigned) && (
                <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Exit Reason</Label>
                  <select data-testid="trade-exit-reason" value={form.exit_reason} onChange={(e) => setForm({...form, exit_reason: e.target.value})} className="w-full h-8 px-2 text-xs font-mono-data rounded-sm border outline-none appearance-none cursor-pointer" style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
                    <option value="" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>— Select —</option>
                    <option value="Expired" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>Expired</option>
                    <option value="Assigned" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>Assigned</option>
                    <option value="Closed Early" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>Closed Early</option>
                    <option value="Rolled" style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>Rolled</option>
                  </select></div>
              )}
              <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Notes</Label><Input data-testid="trade-notes" value={form.outcome_notes} onChange={(e) => setForm({...form, outcome_notes: e.target.value})} placeholder="Trade outcome notes..." className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs" /></div>
              <Button onClick={handleSubmit} className="w-full bg-white text-black hover:bg-gray-200 rounded-sm h-8 text-xs" data-testid="submit-trade-btn">{editingTrade ? "Update Trade" : "Log Trade"}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Open Trades", value: openTrades.length, icon: Clock, color: "var(--hoip-info)" },
          { label: "Closed Trades", value: closedTrades.length, icon: CheckCircle, color: "var(--hoip-text-primary)" },
          { label: "Win Rate", value: winRateLabel, icon: TrendingUp, color: winRate !== null && parseFloat(winRate) >= 50 ? "var(--hoip-profit)" : "var(--hoip-text-tertiary)" },
          { label: "Total P&L", value: `$${totalPnl.toFixed(2)}`, icon: totalPnl >= 0 ? CheckCircle : XCircle, color: totalPnl >= 0 ? "var(--hoip-profit)" : "var(--hoip-loss)" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <div className="flex items-center justify-between mb-1"><span className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</span><Icon className="w-4 h-4" style={{ color }} /></div>
            <span className="font-mono-data text-lg font-medium" style={{ color }}>{value}</span>
          </div>
        ))}
      </div>

      <Tabs defaultValue="open">
        <TabsList className="bg-[#111111] border border-[#27272A] rounded-sm h-9">
          <TabsTrigger value="open" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-open-trades">Open ({openTrades.length})</TabsTrigger>
          <TabsTrigger value="closed" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-closed-trades">Closed ({closedTrades.length})</TabsTrigger>
          <TabsTrigger value="all" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-all-trades">All ({trades.length})</TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-analytics">
            <BarChart3 className="w-3 h-3 mr-1" />Analytics
          </TabsTrigger>
        </TabsList>

        {["open", "closed", "all"].map(tab => {
          const filtered = tab === "open" ? openTrades : tab === "closed" ? closedTrades : trades;
          return (
            <TabsContent key={tab} value={tab} className="mt-4">
              <TradeLogTable trades={filtered} tab={tab} search={search} onSearchChange={setSearch}
                prefs={gridPrefs} onPrefsChange={updateGridPrefs}
                onEdit={handleEdit} onDelete={handleDelete} onCloseTrade={handleCloseTrade} />
            </TabsContent>
          );
        })}

        <TabsContent value="analytics" className="mt-4" data-testid="analytics-tab">
          <AnalyticsDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ═══ Analytics Dashboard ═══
function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfill, setBackfill] = useState(null);
  const [batches, setBatches] = useState([]);
  const [expandedUnd, setExpandedUnd] = useState({});
  const [sourceFilter, setSourceFilter] = useState("");
  const [timeRange, setTimeRange] = useState("");

  const loadAnalytics = (batch = sourceFilter, range = timeRange) => api.get("/trade-log/analytics", {
    params: { ...(batch ? { batch } : {}), ...(range ? { time_range: range } : {}) } })
    .then(({ data: d }) => setData(d))
    .catch(e => console.error("Analytics error:", e))
    .finally(() => setLoading(false));

  useEffect(() => {
    loadAnalytics();
    api.get("/trade-log/import/batches").then(({ data: b }) => setBatches(b.batches || [])).catch(() => {});
    api.get("/trade-log/regime-backfill/status").then(({ data: s }) => {
      if (s.status && s.status !== "never_run") setBackfill(s);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeSource = (v) => {
    const val = v === "all" ? "" : v;
    setSourceFilter(val);
    setLoading(true);
    loadAnalytics(val, timeRange);
  };

  const changeRange = (v) => {
    setTimeRange(v);
    setLoading(true);
    loadAnalytics(sourceFilter, v);
  };

  // Iter194 — poll backfill job while running, refetch analytics on finish.
  useEffect(() => {
    if (backfill?.status !== "running") return;
    const id = setInterval(async () => {
      try {
        const { data: s } = await api.get("/trade-log/regime-backfill/status");
        setBackfill(s);
        if (s.status !== "running") { clearInterval(id); loadAnalytics(); }
      } catch { clearInterval(id); }
    }, 3000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backfill?.status]);

  const runBackfill = async () => {
    try {
      const { data: r } = await api.post("/trade-log/regime-backfill");
      if (r.status === "nothing_to_do") { setBackfill({ status: "done", filled: 0, unavailable: 0, total: 0, nothing: true }); return; }
      setBackfill({ status: "running", processed: 0, total: r.missing });
    } catch (e) { console.error("Backfill start error:", e); }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>;

  // Iter202/203 — analytics filters: source (all/manual/batch) + time range.
  const sourceSelect = (
    <div className="flex items-center gap-3 flex-wrap" data-testid="analytics-source-filter">
      {batches.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Source:</span>
          <select value={sourceFilter || "all"} onChange={(e) => changeSource(e.target.value)}
            className="text-[10px] rounded-sm px-1.5 py-1 border"
            style={{ background: "#0A0A0A", borderColor: "#27272A", color: "var(--hoip-text-primary)" }}
            data-testid="analytics-source-select">
            <option value="all">All sources (combined)</option>
            <option value="manual">Manual entries only</option>
            {batches.map(b => (
              <option key={b.batch_id} value={b.batch_id}>
                {`Import ${b.imported_at?.slice(0, 10) || "?"} · ${b.count} trades (${b.first_date?.slice(0, 10) || "?"} → ${b.last_date?.slice(0, 10) || "?"})`}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex items-center gap-1" data-testid="analytics-range-filter">
        <span className="text-[10px] mr-1" style={{ color: "var(--hoip-text-tertiary)" }}>Range:</span>
        {[["", "Since Inception"], ["1y", "1Y"], ["ytd", "YTD"]].map(([v, label]) => (
          <button key={v || "all"} onClick={() => changeRange(v)}
            className="text-[10px] px-2 py-1 rounded-sm border transition-colors"
            style={{
              borderColor: timeRange === v ? "#3B82F6" : "#27272A",
              color: timeRange === v ? "#3B82F6" : "var(--hoip-text-secondary)",
              background: timeRange === v ? "rgba(59,130,246,0.08)" : "#0A0A0A",
            }}
            data-testid={`analytics-range-${v || "inception"}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (!data?.has_data) return (
    <div className="space-y-4">
      {sourceSelect}
      <div className="text-center py-16 space-y-3" data-testid="analytics-empty">
        <BarChart3 className="w-10 h-10 mx-auto" style={{ color: "var(--hoip-text-tertiary)" }} />
        <p className="text-sm" style={{ color: "var(--hoip-text-secondary)" }}>
          {sourceFilter || timeRange ? "No closed trades in this selection" : "No closed trades yet"}
        </p>
        <p className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
          {sourceFilter || timeRange ? "Try a different source or range filter above." : "Your performance analytics will appear here once you log your first exit."}
        </p>
      </div>
    </div>
  );

  const wrColor = (wr) => wr >= 60 ? "#10B981" : wr >= 40 ? "#F59E0B" : "#EF4444";

  return (
    <div className="space-y-4" data-testid="analytics-dashboard">
      {sourceSelect}
      {/* Summary stats bar */}
      <div className="flex flex-wrap gap-4 border p-3 rounded-sm text-[10px]" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
        {[
          { label: "Total Trades", value: data.total_trades },
          { label: "Win Rate", value: `${data.win_rate}%`, color: data.win_rate >= 50 ? "#10B981" : "#EF4444" },
          { label: "Avg P&L", value: `$${data.avg_ror}`, color: data.avg_ror >= 0 ? "#10B981" : "#EF4444" },
          { label: "Best Strategy", value: data.best_strategy },
          { label: "Worst Strategy", value: data.worst_strategy },
          { label: "Total P&L", value: `$${data.total_pnl?.toLocaleString()}`, color: data.total_pnl >= 0 ? "#10B981" : "#EF4444" },
        ].map(({ label, value, color }) => (
          <div key={label} className="flex-1 min-w-[120px]">
            <div style={{ color: "var(--hoip-text-tertiary)" }}>{label}</div>
            <div className="font-mono-data text-sm font-medium" style={{ color: color || "var(--hoip-text-primary)" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Iter186a — risk-adjusted performance metrics */}
      {data.risk_metrics && (
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
          data-testid="risk-adjusted-metrics">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>Risk-Adjusted Metrics</span>
            <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}
              title="Capital-based figures use the actual Margin Used $ when logged on a trade, otherwise a cash-secured estimate (short strike × 100 × contracts for CSPs, spread width for spreads).">
              Informational · capital basis: {data.risk_metrics.capital_basis} †
            </span>
          </div>
          <div className="flex flex-wrap gap-4 text-[10px]">
            {[
              { label: "Profit Factor", value: data.risk_metrics.profit_factor, testid: "metric-profit-factor",
                color: data.risk_metrics.profit_factor != null ? (data.risk_metrics.profit_factor >= 1 ? "#10B981" : "#EF4444") : null,
                tip: `Gross profit ÷ gross loss ($${data.risk_metrics.gross_profit?.toLocaleString()} / $${data.risk_metrics.gross_loss?.toLocaleString()}). Read alongside Win Rate — a high win rate with a profit factor under 1 means losers outweigh winners.` },
              { label: "Calmar Ratio", value: data.risk_metrics.calmar, testid: "metric-calmar",
                tip: `Annualized return ÷ max drawdown over the same period (max drawdown: $${data.risk_metrics.max_drawdown?.toLocaleString()}, span: ${data.risk_metrics.days_span}d). "—" when no drawdown has been recorded yet.` },
              { label: "MAR Ratio", value: data.risk_metrics.mar, testid: "metric-mar",
                tip: "Compound annual growth rate ÷ max drawdown %, on the estimated deployed-capital base. Related to Calmar but uses CAGR." },
              { label: "Prem/Strike Avg", value: data.risk_metrics.premium_to_strike_avg_pct != null ? `${data.risk_metrics.premium_to_strike_avg_pct}%` : null, testid: "metric-premium-strike",
                tip: `Premium collected ÷ short strike, averaged across ${data.risk_metrics.premium_to_strike_n} credit trade${data.risk_metrics.premium_to_strike_n !== 1 ? "s" : ""} — normalized richness of premium vs capital at risk.` },
              { label: "Return on Capital", value: data.risk_metrics.return_on_capital_pct != null ? `${data.risk_metrics.return_on_capital_pct}%` : null, testid: "metric-roc",
                color: data.risk_metrics.return_on_capital_pct != null ? (data.risk_metrics.return_on_capital_pct >= 0 ? "#10B981" : "#EF4444") : null,
                tip: `Realized P&L ÷ capital deployed (est. $${data.risk_metrics.capital_deployed_est?.toLocaleString()} across closed trades).` },
              { label: "Max Drawdown", value: data.risk_metrics.max_drawdown != null ? `$${data.risk_metrics.max_drawdown.toLocaleString()}` : null, testid: "metric-max-dd",
                color: data.risk_metrics.max_drawdown > 0 ? "#F59E0B" : null,
                tip: "Largest peak-to-trough decline on the cumulative realized-P&L curve." },
              { label: "Jensen's Alpha", value: data.risk_metrics.jensens_alpha_pct != null ? `${data.risk_metrics.jensens_alpha_pct}%` : null, testid: "metric-jensens-alpha",
                color: data.risk_metrics.jensens_alpha_pct != null ? (data.risk_metrics.jensens_alpha_pct >= 0 ? "#10B981" : "#EF4444") : null,
                tip: `Annualized return minus CAPM-expected return: Rf ${data.risk_metrics.risk_free_pct ?? "—"}% + β ${data.risk_metrics.portfolio_beta ?? "—"} × (SPY ${data.risk_metrics.market_return_pct ?? "—"}% − Rf). Beta is capital-weighted across traded tickers (Portfolio Risk Health beta cache).` },
            ].map(({ label, value, color, tip, testid }) => (
              <div key={label} className="flex-1 min-w-[110px] cursor-help" title={tip} data-testid={testid}>
                <div style={{ color: "var(--hoip-text-tertiary)" }}>{label}</div>
                <div className="font-mono-data text-sm font-medium" style={{ color: color || "var(--hoip-text-primary)" }}>
                  {value != null ? value : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Iter186b — Rules Status overrides vs system agreement */}
      {data.override_stats && data.override_stats.tracked_positions > 0 && (
        <div className="border p-3 rounded-sm flex flex-wrap items-center gap-x-6 gap-y-1 text-[10px]"
          style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
          data-testid="override-agreement-strip">
          <span className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>
            Your Decisions vs Rules Status
          </span>
          <span style={{ color: "var(--hoip-text-tertiary)" }}
            title="Positions currently tracked in Position Management with a system-computed Rules Status.">
            Tracked: <b className="font-mono-data" style={{ color: "var(--hoip-text-primary)" }} data-testid="override-tracked">{data.override_stats.tracked_positions}</b>
          </span>
          <span style={{ color: "var(--hoip-text-tertiary)" }}
            title="Positions where you selected a different value than the system's computed status.">
            Overridden: <b className="font-mono-data" style={{ color: data.override_stats.overridden > 0 ? "#F59E0B" : "var(--hoip-text-primary)" }} data-testid="override-count">{data.override_stats.overridden}</b>
          </span>
          {data.override_stats.agreement_rate_pct != null && (
            <span style={{ color: "var(--hoip-text-tertiary)" }}
              title="Share of tracked positions where your recorded decision matches the system's computed status (accepting the default counts as agreement).">
              Agreement rate: <b className="font-mono-data" style={{ color: "var(--hoip-text-primary)" }} data-testid="override-agreement-rate">{data.override_stats.agreement_rate_pct}%</b>
            </span>
          )}
          <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            Informational — tracks how often your decisions align with the rules-based status over time.
          </span>
        </div>
      )}

      {/* 2x2 Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Win Rate by Strategy */}
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
          <div className="text-xs font-bold mb-3" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>Win Rate by Strategy</div>
          {data.wr_by_strategy?.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.wr_by_strategy} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="var(--hoip-text-tertiary)" tickFormatter={v => `${v}%`} />
                <YAxis dataKey="strategy_name" type="category" tick={{ fontSize: 9 }} stroke="var(--hoip-text-tertiary)" width={80} />
                <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px" }}
                  formatter={(v) => [`${v}%`, "Win Rate"]} />
                <Bar dataKey="win_rate" radius={[0, 4, 4, 0]}>
                  {data.wr_by_strategy.map((e, i) => <Cell key={i} fill={wrColor(e.win_rate)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-center py-8" style={{ color: "var(--hoip-text-tertiary)" }}>No data</p>}
        </div>

        {/* Iter203 — By Strategy Structure: linked legs grouped as one unit */}
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
          data-testid="strategy-structures-section">
          <div className="text-xs font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>By Strategy Structure</div>
          <p className="text-[9px] mb-2" style={{ color: "var(--hoip-text-tertiary)" }}>
            Legs opened together (same underlying, open date, expiry — one short + one long) count as ONE unit,
            so a two-leg spread shows one combined win/loss. Asymmetric closes stay grouped, flagged “partial”.
          </p>
          {data.strategy_structures?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full dense-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
                    {["Structure", "Units", "Closed", "Win %", "P&L (realized)", "Partial"].map(h => (
                      <th key={h} className="text-left text-[9px] font-medium py-1 pr-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.strategy_structures.map(s => (
                    <tr key={s.structure} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}
                      data-testid={`structure-row-${s.structure.replace(/\s+/g, "-").toLowerCase()}`}>
                      <td className="text-[10px] py-1 pr-2" style={{ color: "var(--hoip-text-primary)" }}>
                        {s.structure}{s.multi_leg && <span className="ml-1 text-[8px] px-1 rounded-sm" style={{ background: "rgba(59,130,246,0.12)", color: "#3B82F6" }}>2-leg</span>}
                      </td>
                      <td className="text-[10px] font-mono-data pr-2">{s.units}{s.open_units ? <span style={{ color: "var(--hoip-text-tertiary)" }}> ({s.open_units} open)</span> : null}</td>
                      <td className="text-[10px] font-mono-data pr-2">{s.closed_units}</td>
                      <td className="text-[10px] font-mono-data pr-2" style={{ color: s.win_rate == null ? "var(--hoip-text-tertiary)" : wrColor(s.win_rate) }}>
                        {s.win_rate != null ? `${s.win_rate}%` : "—"}
                      </td>
                      <td className="text-[10px] font-mono-data pr-2" style={{ color: s.total_pnl >= 0 ? "#10B981" : "#F87171" }}>
                        ${s.total_pnl.toLocaleString()}
                      </td>
                      <td className="text-[10px] font-mono-data pr-2" style={{ color: s.partial_closures ? "#F59E0B" : "var(--hoip-text-tertiary)" }}
                        title={s.partial_closures ? "Units whose legs closed on different dates or are partially open — grouped as one strategy trade" : undefined}>
                        {s.partial_closures || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-xs text-center py-8" style={{ color: "var(--hoip-text-tertiary)" }}>No data</p>}
        </div>

        {/* P&L by Regime */}
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>P&L by Regime</span>
            <div className="flex items-center gap-2">
              {backfill?.status === "running" && (
                <span className="text-[9px] font-mono-data text-amber-400" data-testid="regime-backfill-progress">
                  Backfilling… {backfill.processed ?? 0}/{backfill.total ?? "?"}
                </span>
              )}
              {backfill?.status === "done" && (
                <span className="text-[9px] font-mono-data text-emerald-500" data-testid="regime-backfill-done"
                  title="Trades whose bar history couldn't support a regime call stay in Unknown — no guessing.">
                  {backfill.nothing ? "Nothing to backfill" : `✓ ${backfill.filled ?? 0} tagged · ${backfill.unavailable ?? 0} unavailable`}
                </span>
              )}
              {backfill?.status === "error" && (
                <span className="text-[9px] font-mono-data text-red-500" data-testid="regime-backfill-error" title={backfill.error || ""}>Backfill failed</span>
              )}
              <button onClick={runBackfill} disabled={backfill?.status === "running"}
                className="text-[9px] px-1.5 h-5 border rounded-sm hover:bg-[#1A1A1A] disabled:opacity-40"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                title="Re-derive regime-at-entry for trades missing it, using each trade's underlying + entry date against the historical HMM timeline."
                data-testid="regime-backfill-btn">
                Backfill regime
              </button>
            </div>
          </div>
          {data.pnl_by_regime?.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.pnl_by_regime}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                <XAxis dataKey="regime" tick={{ fontSize: 9 }} stroke="var(--hoip-text-tertiary)" />
                <YAxis tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="var(--hoip-text-tertiary)" tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px" }}
                  formatter={(v, n, p) => [`$${v} · ${p?.payload?.count ?? "?"} trades`, "Avg P&L"]} />
                <Bar dataKey="avg_pnl" radius={[4, 4, 0, 0]}>
                  {data.pnl_by_regime.map((e, i) => <Cell key={i} fill={e.regime === "Unknown" ? "#71717A" : e.avg_pnl >= 0 ? "#10B981" : "#EF4444"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-center py-8" style={{ color: "var(--hoip-text-tertiary)" }}>No data</p>}
          {data.regime_coverage && (
            <p className="text-[9px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="regime-coverage-note">
              {data.regime_coverage.tagged} closed trade{data.regime_coverage.tagged === 1 ? "" : "s"} tagged with regime-at-entry
              {data.regime_coverage.unknown > 0 ? ` · ${data.regime_coverage.unknown} Unknown (regime data genuinely unavailable)` : ""}.
            </p>
          )}
        </div>

        {/* Cumulative P&L */}
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
          <div className="text-xs font-bold mb-3" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>Cumulative P&L</div>
          {data.cumulative_pnl?.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={data.cumulative_pnl}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                <XAxis dataKey="date" tick={{ fontSize: 8, fontFamily: "JetBrains Mono" }} stroke="var(--hoip-text-tertiary)" interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="var(--hoip-text-tertiary)" tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px" }}
                  formatter={(v) => [`$${v}`, "Cumulative P&L"]} />
                <Line type="monotone" dataKey="pnl" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-center py-8" style={{ color: "var(--hoip-text-tertiary)" }}>No data</p>}
        </div>

        {/* Wheel Cycle Tracker */}
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
          <div className="text-xs font-bold mb-3" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>Wheel Cycle Tracker</div>
          {data.wheel_cycles?.length > 0 ? (
            <div className="overflow-x-auto space-y-2">
              {data.wheel_cycles.map((w, i) => (
                <div key={i} className="border rounded-sm p-2 space-y-1.5" style={{ borderColor: "var(--hoip-border)" }} data-testid={`wheel-cycle-${w.ticker}`}>
                  <div className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-data font-bold">{w.ticker}</span>
                      <span className={`px-1.5 py-0.5 border rounded-sm text-[8px] font-bold ${w.status === "Complete" ? "text-emerald-500 border-emerald-800" : "text-blue-400 border-blue-800"}`}>{w.status}</span>
                      <span style={{ color: "var(--hoip-text-tertiary)" }}>{w.legs} legs</span>
                    </div>
                    <span className="font-mono-data text-emerald-500 font-medium">${w.total_premium.toLocaleString()}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                    <span>Cost Basis: <span className="font-mono-data" style={{ color: "var(--hoip-text-primary)" }}>${w.cost_basis}</span></span>
                    <span>Effective: <span className="font-mono-data" style={{ color: "var(--hoip-text-primary)" }}>${w.effective_cost}</span></span>
                    <span>Capital: <span className="font-mono-data" style={{ color: "var(--hoip-text-primary)" }}>${w.capital?.toLocaleString()}</span></span>
                    <span>Ann. Yield: <span className="font-mono-data font-medium" style={{ color: w.annualised_yield > 20 ? "#10B981" : "#F59E0B" }}>{w.annualised_yield}%</span></span>
                    <span>Entry: <span className="font-mono-data">{w.entry_date}</span></span>
                  </div>
                  {w.leg_details?.length > 0 && (
                    <div className="border-t pt-1 mt-1 space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
                      {w.leg_details.map((l, j) => (
                        <div key={j} className="flex items-center gap-2 text-[9px]">
                          <span className={`w-1.5 h-1.5 rounded-full ${l.status === "closed" ? "bg-emerald-500" : "bg-blue-400"}`} />
                          <span style={{ color: "var(--hoip-text-secondary)" }}>{l.strategy}</span>
                          <span className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{l.entry_date}</span>
                          {l.exit_date && <span className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{"\u2192"} {l.exit_date}</span>}
                          {l.premium > 0 && <span className="font-mono-data text-emerald-500">${l.premium}</span>}
                          <span className={`text-[8px] ${l.status === "closed" ? "text-emerald-500" : "text-blue-400"}`}>{l.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-center py-8" style={{ color: "var(--hoip-text-tertiary)" }}>No Wheel cycles tracked yet. Link trades with a wheel_cycle_id to see them here.</p>
          )}
        </div>
      </div>

      {/* Iter206 — P&L by Underlying (expandable per-trade detail) */}
      {data.pnl_by_underlying?.length > 0 && (
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
          data-testid="pnl-by-underlying-section">
          <div className="text-xs font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>P&L by Underlying</div>
          <p className="text-[9px] mb-2" style={{ color: "var(--hoip-text-tertiary)" }}>
            Every trade on the same underlying (stock legs, short calls, short puts…) summed for the selected source and period. Click a row to see each trade.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
                  {["Underlying", "Trades", "Closed", "Win %", "P&L (realized)"].map(h => (
                    <th key={h} className="text-left text-[9px] font-medium py-1 pr-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.pnl_by_underlying.map(u => (
                  <React.Fragment key={u.ticker}>
                    <tr className="border-b cursor-pointer hover:bg-[rgba(255,255,255,0.03)]" style={{ borderColor: "var(--hoip-divider)" }}
                      onClick={() => setExpandedUnd(p => ({ ...p, [u.ticker]: !p[u.ticker] }))}
                      data-testid={`underlying-row-${u.ticker}`}>
                      <td className="text-[10px] py-1 pr-2 font-medium" style={{ color: "var(--hoip-text-primary)" }}>
                        <span className="inline-block w-3 mr-1 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{expandedUnd[u.ticker] ? "−" : "+"}</span>
                        {u.ticker}
                      </td>
                      <td className="text-[10px] font-mono-data pr-2">{u.count}{u.open ? <span style={{ color: "var(--hoip-text-tertiary)" }}> ({u.open} open)</span> : null}</td>
                      <td className="text-[10px] font-mono-data pr-2">{u.closed}</td>
                      <td className="text-[10px] font-mono-data pr-2" style={{ color: u.win_rate == null ? "var(--hoip-text-tertiary)" : wrColor(u.win_rate) }}>
                        {u.win_rate != null ? `${u.win_rate}%` : "—"}
                      </td>
                      <td className="text-[10px] font-mono-data pr-2" style={{ color: u.total_pnl >= 0 ? "#10B981" : "#F87171" }}>
                        ${u.total_pnl.toLocaleString()}
                      </td>
                    </tr>
                    {expandedUnd[u.ticker] && u.trades.map((t, i) => (
                      <tr key={t.id || i} className="border-b" style={{ borderColor: "var(--hoip-divider)", background: "rgba(255,255,255,0.02)" }}
                        data-testid={`underlying-trade-${u.ticker}-${i}`}>
                        <td className="text-[9px] py-1 pr-2 pl-5" style={{ color: "var(--hoip-text-secondary)" }}>
                          {t.strategy}
                          {(t.short_strike != null || t.long_strike != null) && (
                            <span className="ml-1 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                              {[t.short_strike, t.long_strike].filter(v => v != null).map(v => `$${v}`).join("/")}
                            </span>
                          )}
                          {t.expiry && <span className="ml-1 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>exp {t.expiry}</span>}
                        </td>
                        <td className="text-[9px] font-mono-data pr-2" colSpan={2} style={{ color: "var(--hoip-text-tertiary)" }}>
                          {t.entry_date || "?"}{t.exit_date ? ` → ${t.exit_date}` : ""}
                        </td>
                        <td className="text-[9px] font-mono-data pr-2" style={{ color: t.status === "closed" ? "var(--hoip-text-tertiary)" : "#3B82F6" }}>
                          {t.status}
                        </td>
                        <td className="text-[9px] font-mono-data pr-2" style={{ color: t.pnl == null ? "var(--hoip-text-tertiary)" : (t.pnl >= 0 ? "#10B981" : "#F87171") }}>
                          {t.pnl != null ? `$${t.pnl.toLocaleString()}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Iter194 — Win Rate by Strategy × Regime cross-tab */}
      {data.wr_by_strategy_regime?.length > 0 && (
        <div className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
          data-testid="strategy-regime-matrix">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>Win Rate by Strategy × Regime</span>
            <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Cell: win rate (closed trades) · small samples read with caution</span>
          </div>
          {(() => {
            const ORDER = ["Bullish Trend", "Quiet Range", "Bearish Trend", "Volatile", "Unknown"];
            const present = [...new Set(data.wr_by_strategy_regime.map(x => x.regime))];
            const regimes = ORDER.filter(r => present.includes(r)).concat(present.filter(r => !ORDER.includes(r)));
            const byKey = {};
            data.wr_by_strategy_regime.forEach(x => { byKey[`${x.strategy}|${x.regime}`] = x; });
            const stratTotals = {};
            data.wr_by_strategy_regime.forEach(x => { stratTotals[x.strategy] = (stratTotals[x.strategy] || 0) + x.count; });
            const strats = Object.keys(stratTotals).sort((a, b) => stratTotals[b] - stratTotals[a]);
            return (
              <div className="overflow-x-auto">
                <table className="w-full dense-table">
                  <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <th className="text-left text-[10px] font-medium py-1" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</th>
                    {regimes.map(r => (
                      <th key={r} className="text-right text-[10px] font-medium py-1" style={{ color: r === "Unknown" ? "var(--hoip-text-tertiary)" : "var(--hoip-text-secondary)" }}>{r}</th>
                    ))}
                    <th className="text-right text-[10px] font-medium py-1" style={{ color: "var(--hoip-text-tertiary)" }}>Total n</th>
                  </tr></thead>
                  <tbody>
                    {strats.map(s => (
                      <tr key={s} className="border-b" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`srm-row-${s}`}>
                        <td className="text-xs py-1 capitalize" style={{ color: "var(--hoip-text-primary)" }}>{s.replace(/_/g, " ")}</td>
                        {regimes.map(r => {
                          const c = byKey[`${s}|${r}`];
                          if (!c) return <td key={r} className="text-right text-[10px] font-mono-data py-1" style={{ color: "var(--hoip-text-tertiary)" }}>—</td>;
                          return (
                            <td key={r} className="text-right text-[10px] font-mono-data py-1"
                              title={`${c.count} trade${c.count === 1 ? "" : "s"} · total P&L $${c.total_pnl.toLocaleString()}`}>
                              <span style={{ color: wrColor(c.win_rate) }}>{c.win_rate}%</span>
                              <span className="ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>({c.count})</span>
                            </td>
                          );
                        })}
                        <td className="text-right text-[10px] font-mono-data py-1" style={{ color: "var(--hoip-text-secondary)" }}>{stratTotals[s]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
